import React from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'

import { Text } from '@mxenabled/mxui'
import { Icon } from '@mxenabled/mx-icons'

import {
  Drawer,
  Loader,
  useGlobalCopyStore,
  useGlobalUiStore,
  useTransactionStore,
} from '../../../common'
import { exportTransactionsToCSV } from '../../../core/utils/TransactionUtils'
import { TransactionList } from '../../../transactions'

import type { BarDataProps } from '../../types/Charts/BarChart'

import { ChartDrawerTemplate, type DrawerBeat } from './ChartDrawerTemplate/ChartDrawerTemplate'
import type { TipSectionProps } from './ChartDrawerTemplate/TipSection'
import { BarChartV2 } from './Charts'

interface ChartWithTransactionDrillDownProps {
  ariaLabel: string
  barChartProps: React.ComponentProps<typeof BarChartV2>
  beat: DrawerBeat
  data: BarDataProps[]
  defaultSelectedIndex?: number
  icon?: React.ReactNode
  isOpen?: boolean
  onClose: () => void
  onTabChange?: (index: number) => void
  tipSection?: TipSectionProps
  title: string
  transactionListProps?: React.ComponentProps<typeof TransactionList>
}

const ChartWithTransactionDrillDown = ({
  ariaLabel,
  barChartProps,
  beat,
  data,
  defaultSelectedIndex = 0,
  icon,
  isOpen = false,
  onClose,
  onTabChange,
  tipSection,
  title,
  transactionListProps = {},
}: ChartWithTransactionDrillDownProps) => {
  const { common: commonCopy, transactions: transactionsCopy } = useGlobalCopyStore()
  const { isCopyLoaded } = useGlobalUiStore()
  const { sortedTransactionsWithSplits, tags } = useTransactionStore()

  if (!isCopyLoaded) return <Loader />

  return (
    <Drawer ariaLabelClose={commonCopy.close_aria} isOpen={isOpen} onClose={onClose} title={title}>
      <ChartDrawerTemplate
        ariaLabel={ariaLabel}
        beat={beat}
        chart={(selectedTab, onBarClick) => (
          <Box sx={{ pt: 16 }}>
            <BarChartV2
              {...barChartProps}
              onBarClick={(i) => {
                onBarClick(i)
                barChartProps.onBarClick?.(i)
              }}
              selectedTabIndex={selectedTab}
            />
          </Box>
        )}
        data={data}
        defaultSelectedIndex={defaultSelectedIndex}
        icon={icon}
        onTabChange={onTabChange}
        tipSection={tipSection}
      >
        {/* Tab selection will be added here later */}
        {() => {
          return (
            <React.Fragment>
              <Stack
                sx={{
                  alignItems: 'center',
                  borderBottomColor: 'divider',
                  borderBottomStyle: 'solid',
                  borderBottomWidth: 1,
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  pb: 4,
                  pt: 16,
                  px: 24,
                }}
              >
                <Text bold={true} variant="body1">
                  {transactionsCopy.transactions_title}
                </Text>
                {(transactionListProps?.transaction || sortedTransactionsWithSplits.length > 0) && (
                  <Button
                    onClick={() => {
                      exportTransactionsToCSV(
                        transactionListProps?.transaction || sortedTransactionsWithSplits,
                        tags,
                      )
                    }}
                    startIcon={<Icon name="ios_share" />}
                    sx={{ height: 28 }}
                  >
                    <Text bold={true} variant="body2">
                      {transactionsCopy.export_csv_btn}
                    </Text>
                  </Button>
                )}
              </Stack>
              <TransactionList {...transactionListProps} />
            </React.Fragment>
          )
        }}
      </ChartDrawerTemplate>
    </Drawer>
  )
}

export default ChartWithTransactionDrillDown
